import {
  DAY_SCROLL_OFFSET,
  MAX_RETRIES,
  SCHEDULE_DATE_FORMAT,
  SCHEDULE_DAY_DISPLAY_FORMAT,
} from '../classfinder.constants';

import { Languages } from '@/common/utils/constants';
import dayjs from '@/common/lib/dayjs';

/**
 * Converts UTC datetime string to user's local timezone
 * @param utcDateTimeString - UTC datetime string (e.g., "2024-01-15T10:00:00Z")
 * @returns ISO string in local timezone
 */
export const convertUTCToLocal = (utcDateTimeString: string): string => {
  return dayjs.utc(utcDateTimeString).local().toISOString();
};

/**
 * Formats a date with uppercase day name and proper ordinal suffix
 * Displays like: "TUESDAY 1ST" or "WEDNESDAY 3RD"
 */
export const formatLocalDayWithOrdinal = (
  date: dayjs.Dayjs,
  lang: Languages
): string => {
  if (lang === Languages.nl) {
    return date.locale('nl').format('dddd D').toUpperCase();
  }

  return date.format(SCHEDULE_DAY_DISPLAY_FORMAT).toUpperCase();
};

/**
 * Generates a day element ID from a date
 */
export const getDayElementId = (date: dayjs.Dayjs) => {
  return `day-${date.format(SCHEDULE_DATE_FORMAT)}`;
};

/**
 * Handles scrolling to the specified day element
 */
export const scrollToDay = (
  dayRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>,
  targetKey: string,
  isScrollingRef: React.RefObject<boolean>,
  onComplete?: () => void
) => {
  const scrollTarget = dayRefs.current[targetKey];

  if (scrollTarget) {
    const rect = scrollTarget.getBoundingClientRect();
    const absoluteTop = window.scrollY + rect.top;
    const offsetTop = absoluteTop - DAY_SCROLL_OFFSET;

    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth',
    });

    setTimeout(() => {
      isScrollingRef.current = false;
      onComplete?.();
    }, 500);

    return true;
  }

  return false;
};

/**
 * Retries scrolling to the day element
 */
export const retryScroll = (
  dayRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>,
  targetKey: string,
  isScrollingRef: React.RefObject<boolean>,
  retries = 0
) => {
  const scrollSuccess = scrollToDay(dayRefs, targetKey, isScrollingRef);

  if (!scrollSuccess && retries < MAX_RETRIES) {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
    setTimeout(() => {
      retryScroll(dayRefs, targetKey, isScrollingRef, retries + 1);
    }, 300);
  } else if (!scrollSuccess) {
    console.warn('Max retries reached. Could not find day.');
    isScrollingRef.current = false;
  }
};

/**
 * Sets up the scroll event listener to detect active date
 */
export const setupScrollListener = (
  dayRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>,
  setActiveDate: React.Dispatch<React.SetStateAction<string>>
) => {
  let lastScrollY = window.scrollY;

  const handleScroll = () => {
    const refEntries = Object.entries(dayRefs.current);
    if (refEntries.length === 0) return;

    const filterButton = document.getElementById('filter-button');
    if (!filterButton) return;

    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY;
    lastScrollY = currentScrollY;

    const buttonRect = filterButton.getBoundingClientRect();
    const referenceLine = buttonRect.bottom + 50;

    let targetDay: string | null = null;

    // Sort days by date to ensure proper order
    const sortedDays = refEntries
      .filter(([, ref]) => ref !== null)
      .sort(([dayA], [dayB]) => dayjs(dayA).valueOf() - dayjs(dayB).valueOf());

    if (isScrollingDown) {
      // When scrolling down: find the next day when previous day is < 50px below button
      for (let i = 0; i < sortedDays.length - 1; i++) {
        const [, currentRef] = sortedDays[i];
        const [nextDay] = sortedDays[i + 1];

        if (currentRef) {
          const rect = currentRef.getBoundingClientRect();
          if (rect.bottom < referenceLine) {
            targetDay = nextDay;
          }
        }
      }
    } else {
      // When scrolling up: find the previous day when current day is > 50px below button
      for (let i = sortedDays.length - 1; i > 0; i--) {
        const [, currentRef] = sortedDays[i];
        const [prevDay] = sortedDays[i - 1];

        if (currentRef) {
          const rect = currentRef.getBoundingClientRect();
          if (rect.top > referenceLine) {
            targetDay = prevDay;
          }
        }
      }
    }

    // If no specific day found, fall back to closest visible day
    if (!targetDay) {
      let closestDay: string | null = null;
      let smallestDistance = Infinity;

      sortedDays.forEach(([day, ref]) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const distance = Math.abs(rect.top - referenceLine);

          if (rect.top < window.innerHeight && rect.bottom > 0) {
            if (distance < smallestDistance) {
              smallestDistance = distance;
              closestDay = day;
            }
          }
        }
      });

      targetDay = closestDay;
    }

    if (targetDay) {
      const active = dayjs(targetDay);
      if (active.isValid()) {
        setActiveDate((prev) => {
          if (!prev || !dayjs(prev).isSame(active, 'day')) {
            return active.toISOString();
          }
          return prev;
        });
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll);

  return () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);
  };
};

/**
 * Rounds up the current time to the next hour
 * @returns {string} Time in HH:mm format
 * @example
 * // If current time is 13:45, returns "14:00"
 * // If current time is 16:02, returns "17:00"
 * // If current time is 23:30, returns "00:00"
 * // If current time is exactly on the hour (e.g., 14:00), returns "14:00"
 */
export const getNextHourTime = (): string => {
  const now = dayjs();

  const currentHour = now.hour();
  const currentMinute = now.minute();

  // If we're past the current hour, round up to next hour
  const targetHour = currentMinute > 0 ? currentHour + 1 : currentHour;

  // If we're at 23:xx, wrap to 00:00
  const hour = targetHour > 23 ? 0 : targetHour;

  return dayjs().hour(hour).minute(0).format('HH:mm');
};
